import merge from 'lodash/merge'
import currency from 'currency.js'
import { Chart as ChartJS, BarController, BarElement, Legend, LinearScale, TimeScale, Title } from 'chart.js'
import { Bar } from 'react-chartjs-2'
import 'chartjs-adapter-date-fns'
import { getInvestmentByYear } from '../util'

ChartJS.register(BarElement, BarController, Legend, LinearScale, TimeScale, Title)

const getCommonOptions = () => {
  return {
    responsive: true,
    aspectRatio: 1.5,
    plugins: {
      title: {
        display: true,
      },
      legend: {
        position: 'bottom',
      },
    },
    scales: {
      x: {
        type: 'linear',
        offset: false,
        title: {
          display: true,
          text: 'Payment #',
        },
        grid: {
          drawTicks: false,
        },
        ticks: {
          precision: 0,
          count: 3,
          callback: (value, index) => (index === 0 ? '1' : value),
        },
      },
      y: {
        type: 'linear',
        beginAtZero: true,
        offset: false,
        title: {
          display: true,
          text: '$',
        },
        ticks: {
          count: 5,
        },
      },
    },
  }
}

export function BarChart({ data, options, title }) {
  if (title) {
    options.plugins = { title: { text: title } }
  }

  const mergedOptions = merge({}, getCommonOptions(), options)

  return <Bar data={data} options={mergedOptions} />
}

export function EquityBalanceInterestChart({ amortization, title }) {
  const paymentSchedule = amortization.paymentSchedule
  const events = paymentSchedule.filter((event) => !event.hideFromChart)

  const data = {
    labels: events.map((event) => event.eventNum),
    datasets: [
      {
        label: 'Equity Earned',
        backgroundColor: 'red',
        data: events.map((event) => amortization.totalPrincipal - event.balance.value),
      },
      {
        label: 'Balance Owed',
        backgroundColor: 'green',
        data: events.map((event) => event.balance.value),
      },
      {
        label: 'Total Interest Paid',
        backgroundColor: 'blue',
        data: events.reduce((totals, event) => {
          const lastTotal = totals.length ? totals[totals.length - 1] : 0
          const newTotal = lastTotal + event.interest.value
          totals.push(newTotal)

          return totals
        }, []),
      },
    ],
  }

  const options = {
    plugins: {
      title: {
        text: title,
      },
    },
    scales: {
      x: {
        max: events.length,
      },
    },
  }

  return <BarChart data={data} options={options} />
}

export function BalanceInterestChart({ amortization, title }) {
  const paymentSchedule = amortization.paymentSchedule

  const data = {
    labels: paymentSchedule.map((event) => event.eventNum),
    datasets: [
      {
        label: 'Balance Owed',
        backgroundColor: 'red',
        data: paymentSchedule.map((event) => event.balance.value),
      },
      {
        label: 'Total Interest Paid',
        backgroundColor: 'blue',
        data: paymentSchedule.reduce((totals, event) => {
          const lastTotal = totals.length ? totals[totals.length - 1] : 0
          const newTotal = lastTotal + event.interest.value
          totals.push(newTotal)

          return totals
        }, []),
      },
    ],
  }

  const options = {
    plugins: {
      title: {
        text: title,
      },
    },
    scales: {
      x: {
        max: paymentSchedule.length,
      },
    },
  }

  return <BarChart data={data} options={options} />
}

export function BreakdownChart({ amortization, title }) {
  const paymentSchedule = amortization.paymentSchedule
  const events = paymentSchedule.filter((event) => !event.hideFromChart)

  const data = {
    labels: events.map((event) => event.eventNum),
    datasets: [
      {
        label: 'Principal Portion of Payment',
        backgroundColor: 'green',
        data: events.map((event) => event.principal.value),
      },
      {
        label: 'Interest Portion of Payment',
        backgroundColor: 'blue',
        data: events.map((event) => event.interest.value),
      },
    ],
  }

  const options = {
    plugins: {
      title: {
        text: title,
      },
    },
    scales: {
      x: {
        stacked: true,
        max: events.length,
      },
      y: {
        stacked: true,
      },
    },
  }

  return <BarChart data={data} options={options} />
}

export function InflationChart({ inflation, title }) {
  const schedule = inflation.schedule

  const data = {
    labels: schedule.map((event) => event.eventNum),
    datasets: [
      {
        backgroundColor: 'red',
        data: schedule.map((event) => event.value),
      },
    ],
  }

  const options = {
    plugins: {
      title: {
        text: title,
      },
      legend: {
        display: false,
      },
    },
    scales: {
      x: {
        title: {
          text: 'Year',
        },
        max: schedule.length,
      },
    },
  }

  return <BarChart data={data} options={options} />
}

export function InvestmentChart({ investment, title }) {
  const schedule = investment.schedule

  const data = {
    labels: schedule.map((event) => event.date),
    datasets: [
      {
        label: 'Present Value',
        backgroundColor: 'blue',
        data: schedule.map(() => currency(investment.presentValue).value),
      },
      {
        label: 'Interest',
        backgroundColor: 'green',
        data: schedule.map((event) => event.totalInterest.value),
      },
    ],
  }

  const options = {
    plugins: {
      title: {
        text: title,
      },
    },
    scales: {
      x: {
        type: 'time',
        stacked: true,
        offset: true,
        title: {
          text: 'At Date',
        },
        ticks: {
          callback: (value) => value,
        },
      },
      y: {
        stacked: true,
      },
    },
  }

  return <BarChart data={data} options={options} />
}

export function EndOfYearChart({ investment, title }) {
  const schedule = getInvestmentByYear(investment, false)

  const data = {
    labels: schedule.map((_, i) => i + 1),
    datasets: [
      {
        backgroundColor: 'green',
        data: schedule.map((group) => group.totals.balance.value),
      },
    ],
  }

  const options = {
    plugins: {
      title: {
        text: title,
      },
      legend: {
        display: false,
      },
    },
    scales: {
      x: {
        title: {
          offset: true,
          text: 'Year',
        },
      },
    },
  }

  return <BarChart data={data} options={options} />
}

export function SavingsChart({ investment, title }) {
  const schedule = investment.schedule

  const data = {
    labels: schedule.map((event) => event.eventNum),
    datasets: [
      {
        label: 'Starting Balance',
        backgroundColor: 'blue',
        data: schedule.map(() => currency(investment.presentValue).value),
      },
      {
        label: 'Deposits Made',
        backgroundColor: 'red',
        data: schedule.map((event) => currency(event.totalDeposit).value),
      },
      {
        label: 'Interest',
        backgroundColor: 'green',
        data: schedule.map((event) => event.totalInterest.value),
      },
    ],
  }

  const options = {
    plugins: {
      title: {
        text: title,
      },
    },
    scales: {
      x: {
        stacked: true,
        title: {
          text: 'Deposit #',
        },
      },
      y: {
        stacked: true,
      },
    },
  }

  return <BarChart data={data} options={options} />
}
